import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { EmailBlock, EmailBlockType } from './types';
import { emailBuilderBlockBackground } from './blockStyles';
import { InnerDropZone } from './DropZone';
import { RowActions } from './RowActions';

export const ColumnBlock: React.FC<{
  block: EmailBlock;
  isSelected: boolean;
  isDragging: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  renderChild: (child: EmailBlock) => React.ReactNode;
  insertAt: (index: number, data: any, columnIdx?: number) => void;
}> = ({ block, isSelected, isDragging, onSelect, onDelete, onDuplicate, onDragStart, onDragEnd, renderChild, insertAt }) => {
  const common = {
    px: 2,
    py: 1.5,
    border: '1px solid',
    borderRadius: 2,
    mx: 1,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: emailBuilderBlockBackground,
    cursor: 'move',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: isSelected ? 'primary.main' : 'primary.light'
    }
  } as const;
  
  const columns = block.columns || [];
  const columnCount = block.columnCount || 2;
  
  return (
    <Box sx={{ ...common, borderStyle: 'dashed' }}>
      <Box 
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5, cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <Typography variant="subtitle2" sx={{ mb: 0, wordBreak: 'break-word' }}>
          Column ({columnCount}) : {block.title || 'Column'}
        </Typography>
        <RowActions onDelete={onDelete} onDuplicate={onDuplicate} />
      </Box>
      
      <Box sx={{ display: 'flex', gap: 1, minHeight: '60px' }}>
        {Array.from({ length: columnCount }, (_, columnIdx) => (
          <Box key={columnIdx} sx={{ flex: 1, border: '1px dashed #ccc', borderRadius: 1, p: 1, minHeight: '50px' }}>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', textAlign: 'center', color: 'text.secondary' }}>
              Column {columnIdx + 1}
            </Typography>
            <InnerDropZone isDragging={isDragging} onDrop={(d) => insertAt(0, d, columnIdx)} onDropComplete={onDragEnd} />
            {columns[columnIdx]?.map((child, idx) => (
              <React.Fragment key={child.id}>
                {renderChild(child)}
                <InnerDropZone isDragging={isDragging} onDrop={(d) => insertAt(idx + 1, d, columnIdx)} onDropComplete={onDragEnd} />
              </React.Fragment>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const ColumnBlockDialog: React.FC<{
  open: boolean;
  draft: EmailBlock;
  setDraft: (next: EmailBlock) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, draft, setDraft, onCancel, onConfirm }) => {
  if (draft.type !== 'columns') return null;

  const handleColumnCountChange = (count: number) => {
    const currentColumns = draft.columns || [];
    const newColumns = Array.from({ length: count }, (_, idx) => currentColumns[idx] || []);
    setDraft({ ...draft, columnCount: count, columns: newColumns });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Column ({draft.columnCount || 2}-4)</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={draft.title || ''}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            fullWidth
            size="small"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Columns</InputLabel>
            <Select
              value={draft.columnCount || 2}
              onChange={(e) => handleColumnCountChange(Number(e.target.value))}
              label="Columns"
            >
              <MenuItem value={2}>2 Columns</MenuItem>
              <MenuItem value={3}>3 Columns</MenuItem>
              <MenuItem value={4}>4 Columns</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Padding (px)"
            type="number"
            value={draft.columnPadding ?? 8}
            onChange={(e) => setDraft({ ...draft, columnPadding: Number(e.target.value) })}
            fullWidth
            size="small"
            inputProps={{ min: 0, max: 50 }}
          />

          <TextField
            label="Background Color"
            type="color"
            value={draft.columnBackground || '#ffffff'}
            onChange={(e) => setDraft({ ...draft, columnBackground: e.target.value })}
            fullWidth
            size="small"
            InputProps={{
              style: { height: '40px' }
            }}
          />

          {/* Column Preview */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>Preview:</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {Array.from({ length: draft.columnCount || 2 }, (_, idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: 1,
                    padding: `${draft.columnPadding ?? 8}px`,
                    backgroundColor: draft.columnBackground || '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Column {idx + 1}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
        <Button color="success" variant="contained" onClick={onConfirm}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

// Column block instantiation function
export const instantiateColumnBlock = (): EmailBlock => {
  return {
    id: '',
    type: 'columns',
    title: 'Column',
    columnCount: 2,
    columnPadding: 8,
    columnBackground: '#ffffff',
    columns: [[], []]
  };
};

// Column block HTML rendering function
export const renderColumnBlockToHtml = (block: EmailBlock, renderChild: (child: EmailBlock) => string): string => {
  const columns = block.columns || [];
  const columnCount = block.columnCount || 2;
  const padding = block.columnPadding ?? 8;
  const backgroundColor = block.columnBackground || '#ffffff';
  
  // Generate column HTML - extract content and styles from table rows
  const columnHtml = Array.from({ length: columnCount }, (_, idx) => {
    const columnBlocks = columns[idx] || [];
    let columnContent = '';
    
    // Process each block in the column
    columnBlocks.forEach(child => {
      const childHtml = renderChild(child);
      // Extract both the style and content from the <tr><td>...</td></tr> structure
      const styleMatch = childHtml.match(/<tr><td style="([^"]*)">(.*?)<\/td><\/tr>/s);
      if (styleMatch) {
        const style = styleMatch[1];
        const content = styleMatch[2];
        // Wrap content in a div that preserves the child's styles
        columnContent += `<div style="${style}">${content}</div>`;
      } else {
        // Fallback: extract content without style
        const contentMatch = childHtml.match(/<tr><td[^>]*>(.*?)<\/td><\/tr>/s);
        if (contentMatch) {
          columnContent += contentMatch[1];
        } else {
          columnContent += childHtml;
        }
      }
    });
    
    return `<td style="padding:${padding}px; background-color:${backgroundColor}; vertical-align:top;" width="${100/columnCount}%">${columnContent}</td>`;
  }).join('');
  
  return `<tr><td><table role="presentation" width="100%" cellPadding="0" cellSpacing="0"><tbody><tr>${columnHtml}</tr></tbody></table></td></tr>`;
};
